import * as React from 'react';

import {
  PlanType,
  StripeProductLookupKeys,
} from '@showbie-socrative/socrative-utils';
import cx from 'classnames';
import { useAtomValue } from 'jotai';

import { CreateCheckoutSessionEventAdapter } from '~analytics/adapters/CreateCheckoutSessionEventAdapter';
import { CreatePortalSessionEventAdapter } from '~analytics/adapters/CreatePortalSessionEventAdapter';
import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { BillingPeriod } from '~teacher/components/PricingTable/BillingPeriod';
import {
  useCurrentPlan,
  useIsTrialing,
  useSelectedTeamHasDisputedSubscription,
  useSelectedTeamPermissionQuery,
} from '~teacher/components/PricingTable/hooks';
import { useSelectedTeamHasActiveSubscriptionQuery } from '~teacher/components/PricingTable/hooks/useHasTeamActiveSubscriptionQuery';
import { usePricingPlans } from '~teacher/hook/usePlanPrices';

import {
  createCheckoutSessionUrl,
  createCustomerPortalSessionUrl,
} from '../../../api/sales/salesUrls';

interface PrimaryButtonProps {
  targetPlan: PlanType;
  billingPeriod: BillingPeriod;
  seats?: number;
}

// TODO: This component has become nightmareishly complex
export const PrimaryButton = ({
  targetPlan,
  billingPeriod,
  seats,
}: PrimaryButtonProps) => {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const currentPlanQuery = useCurrentPlan();
  const usingTrialPlanQuery = useIsTrialing();
  const pricingPlanQuery = usePricingPlans({
    select: (data) => {
      const pricingPlan = data[targetPlan];
      const { trialDays } = pricingPlan;

      const lookupKey =
        billingPeriod === BillingPeriod.Monthly
          ? pricingPlan.monthly.lookupKey
          : pricingPlan.yearly.lookupKey;

      return { lookupKey, trialDays };
    },
  });

  if (
    !pricingPlanQuery.data ||
    usingTrialPlanQuery.data === undefined ||
    !currentPlanQuery.data
  ) {
    return null;
  }

  const { trialDays, lookupKey } = pricingPlanQuery.data;
  const usingTrialPlan = usingTrialPlanQuery.data;
  const currentPlan = currentPlanQuery.data;

  if (
    currentPlan === PlanType.Organization ||
    currentPlan === PlanType.Complete
  ) {
    return null;
  }

  return (
    <Checkout
      seats={seats}
      lookupKey={lookupKey}
      teamId={selectedTeamId}
      currentPlan={currentPlan}
      targetPlan={targetPlan}
      trialDays={trialDays}
      usingTrialPlan={usingTrialPlan}
    />
  );
};

const planMap: Record<PlanType, number> = {
  [PlanType.Free]: 0,
  [PlanType.FreeLegacy]: 0,
  // Discontinued plans
  [PlanType.Starter]: 0,
  [PlanType.LegacyPro]: 0,
  // Active plans
  [PlanType.Essentials]: 1,
  [PlanType.Pro]: 2,
  [PlanType.Organization]: 3,
  [PlanType.Complete]: 3,
};

export function calcIsUpgrading({
  currentPlan,
  targetPlan,
}: {
  currentPlan: PlanType;
  targetPlan: PlanType;
}) {
  const currentPlanRank = planMap[currentPlan];
  const targetPlanRank = planMap[targetPlan];

  return targetPlanRank > currentPlanRank;
}

export function calcIsDowngrading({
  currentPlan,
  targetPlan,
}: {
  currentPlan: PlanType;
  targetPlan: PlanType;
}) {
  const currentPlanRank = planMap[currentPlan];
  const targetPlanRank = planMap[targetPlan];

  return targetPlanRank < currentPlanRank;
}
interface CheckoutProps {
  lookupKey: StripeProductLookupKeys;
  teamId: string;
  currentPlan: PlanType;
  targetPlan: PlanType;
  trialDays: number;
  usingTrialPlan: boolean;
  seats?: number;
}

function Checkout({
  lookupKey,
  teamId,
  currentPlan,
  targetPlan,
  trialDays,
  usingTrialPlan,
  seats,
}: CheckoutProps) {
  const teamSubscriptionsPermissionQuery = useSelectedTeamPermissionQuery();
  const doesTeamHaveActiveSubscriptionQuery =
    useSelectedTeamHasActiveSubscriptionQuery();
  const doesTeamHaveDisputedSubscriptionQuery =
    useSelectedTeamHasDisputedSubscription();

  const isUsingFreePlan = currentPlan === PlanType.Free;
  const isCurrentPlan = currentPlan === targetPlan;
  const isUpgrading = calcIsUpgrading({ currentPlan, targetPlan });
  const isDowngrading = calcIsDowngrading({ currentPlan, targetPlan });

  if (
    !teamSubscriptionsPermissionQuery.data ||
    doesTeamHaveActiveSubscriptionQuery.data === undefined ||
    doesTeamHaveDisputedSubscriptionQuery.data
  ) {
    return null;
  }

  const hasActiveSubscription = doesTeamHaveActiveSubscriptionQuery.data;
  const formAction =
    (isUpgrading || isDowngrading || isCurrentPlan) && hasActiveSubscription
      ? createCustomerPortalSessionUrl().href
      : createCheckoutSessionUrl().href;
  let sendAnalytics =
    isUpgrading && !isUsingFreePlan
      ? () => {
          new CreatePortalSessionEventAdapter({
            source: 'pricing_table__upgrade',
            team_id: teamId,
          }).sendAnalytics();
        }
      : () => {
          new CreateCheckoutSessionEventAdapter({
            source: 'pricing_table',
            team_id: teamId,
          }).sendAnalytics();
        };
  if (isCurrentPlan)
    sendAnalytics = () => {
      new CreatePortalSessionEventAdapter({
        source: 'pricing_table__manage',
        team_id: teamId,
      }).sendAnalytics();
    };

  const shouldShowTrial = currentPlan === PlanType.Free && trialDays > 0;

  const trialButton = (
    <SmallButton
      type="submit"
      appearance="primary"
      isDisabled={!shouldShowTrial}
      className={'w-full mb-2'}
    >
      {translate('Try For {0} Days').format(trialDays)}
    </SmallButton>
  );

  const purchaseButtonLabel = (() => {
    if (isCurrentPlan) {
      return translate('Manage Plan');
    } else if (shouldShowTrial) {
      return translate('Buy Now');
    } else if (isDowngrading && usingTrialPlan) {
      return translate('Upgrade');
    } else if (isDowngrading) {
      return translate('Downgrade');
    } else if (isUpgrading) {
      return translate('Upgrade');
    } else {
      return translate('Upgrade');
    }
  })();

  const purchaseButton = (
    <div className="flex flex-col gap-1">
      <SmallButton type="submit" appearance="primary" className={'w-full mb-2'}>
        {purchaseButtonLabel}
      </SmallButton>
    </div>
  );

  const purchaseTextButton = (
    <div className={cx(shouldShowTrial && 'flex items-center justify-center')}>
      <SmallButton
        type="submit"
        appearance="text"
        className={cx(
          'flex underline justify-center',
          !shouldShowTrial && 'w-full'
        )}
      >
        {purchaseButtonLabel}
      </SmallButton>
    </div>
  );

  const { canOpenStripeCheckout, canOpenStripePortal } =
    teamSubscriptionsPermissionQuery.data;

  if (
    formAction === createCustomerPortalSessionUrl().href &&
    !canOpenStripePortal
  ) {
    return null;
  }

  if (
    formAction === createCheckoutSessionUrl().href &&
    !canOpenStripeCheckout
  ) {
    return null;
  }

  return (
    <>
      <CheckoutForm
        seats={!hasActiveSubscription ? seats : undefined}
        isVisible={shouldShowTrial}
        formAction={formAction}
        lookupKey={lookupKey}
        teamId={teamId}
        isSwitchingPlan={
          hasActiveSubscription && (isUpgrading || isDowngrading)
        }
        trialDays={trialDays}
        shouldShowTrial={shouldShowTrial}
        formButton={trialButton}
        onSubmit={sendAnalytics}
      />
      <CheckoutForm
        seats={!hasActiveSubscription ? seats : undefined}
        isVisible={true}
        formAction={formAction}
        lookupKey={lookupKey}
        teamId={teamId}
        isSwitchingPlan={
          hasActiveSubscription && (isUpgrading || isDowngrading)
        }
        trialDays={trialDays}
        shouldShowTrial={false}
        formButton={shouldShowTrial ? purchaseTextButton : purchaseButton}
        onSubmit={sendAnalytics}
      />
    </>
  );
}

interface CheckoutFormProps {
  isVisible: boolean;
  formAction: string;
  lookupKey: StripeProductLookupKeys;
  teamId: string;
  isSwitchingPlan: boolean;
  trialDays: number;
  shouldShowTrial: boolean;
  formButton: React.ReactNode;
  onSubmit: () => void;
  seats: number;
}

export function CheckoutForm({
  isVisible,
  formAction,
  lookupKey,
  teamId,
  isSwitchingPlan,
  trialDays,
  shouldShowTrial,
  formButton,
  onSubmit,
  seats,
}: CheckoutFormProps) {
  if (!isVisible) return null;
  return (
    <form action={formAction} onSubmit={onSubmit} method="POST">
      {formButton}
      <input
        name="lookup_key"
        id="lookup_key"
        value={lookupKey}
        readOnly
        hidden
      />
      {isSwitchingPlan ? (
        <input name="action" id="action" value="switch_plan" readOnly hidden />
      ) : null}
      {shouldShowTrial ? (
        <input
          name="trial_days"
          id="trial_days"
          value={trialDays}
          readOnly
          hidden
        />
      ) : null}
      <input name="team_id" id="team_id" value={teamId} readOnly hidden />
      {seats ? (
        <input name="seats" id="seats" value={seats} readOnly hidden />
      ) : null}
    </form>
  );
}
